import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdMic, MdStop, MdChevronLeft, MdChevronRight, MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import {
  LiveAvatarSession,
  SessionState,
  SessionEvent,
  VoiceChatState,
  VoiceChatEvent,
  AgentEventsEnum,
} from '@heygen/liveavatar-web-sdk';
import { useAdvisor } from '../context/AdvisorContext';
import './Advisor.css';

// ─────────────────────────────────────────────────────────────────────────────
// ActiveAvatarSession — drives one LiveAvatarSession lifecycle. Mounts when
// liveAvatarToken is non-empty, tears down when it goes away or this component
// unmounts. Imperative API (the React-context wrapper used in the SDK demo
// is demo-local code, not part of the published package).
// ─────────────────────────────────────────────────────────────────────────────
function ActiveAvatarSession({ isFullscreen, onToggleFullscreen }) {
  const ctx = useAdvisor();
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;  // keep handlers reading the latest context without resubscribing

  const videoRef    = useRef(null);
  const canvasRef     = useRef(null);
  const sessionRef    = useRef(null);
  const transcriptRef = useRef(null);

  const [sessionState,  setSessionState]  = useState(SessionState.INACTIVE);
  const [voiceChatState, setVoiceChatState] = useState(VoiceChatState.INACTIVE);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  const [streamReady,   setStreamReady]   = useState(false);

  const token = ctx.liveAvatarToken;
  // Avatars rendered against a real studio scene don't need chromakey; render
  // the <video> directly. Greenscreen avatars opt in via advisors[i].chromakey.
  const useChromakey = ctx.selectedAdvisor?.chromakey === true;

  // One-shot session lifecycle: create on mount/token-change, tear down on cleanup.
  useEffect(() => {
    if (!token) return;
    let disposed = false;

    const session = new LiveAvatarSession(token, { voiceChat: true });
    sessionRef.current = session;

    session.on(SessionEvent.SESSION_STATE_CHANGED, (state) => {
      if (disposed) return;
      console.log('[onboarding] session.state', state);
      setSessionState(state);
      if (state === SessionState.DISCONNECTED) {
        // Hand off to the bootstrap path. finalize is idempotent.
        ctxRef.current.finalizeOnboardingTranscript?.();
      }
    });
    session.on(SessionEvent.SESSION_STREAM_READY, () => {
      if (disposed) return;
      console.log('[onboarding] stream.ready');
      setStreamReady(true);
      const v = videoRef.current;
      if (v) {
        try { session.attach(v); } catch (e) { console.warn('attach failed:', e); }
        // Some browsers don't autoplay an opacity-0 element until you ask;
        // call play() explicitly to be safe. Audio still routes through this
        // element, so this is what produces sound too.
        const tryPlay = () => v.play().catch((e) => console.warn('video.play failed:', e));
        if (v.readyState >= 1) tryPlay();
        else v.addEventListener('loadedmetadata', tryPlay, { once: true });
      }
      // Auto-start voice chat so the user can just speak.
      session.voiceChat.start().catch((e) => console.warn('voiceChat.start failed:', e));
    });
    session.voiceChat.on(VoiceChatEvent.STATE_CHANGED, (s) => {
      console.log('[onboarding] voiceChat.state', s);
      if (!disposed) setVoiceChatState(s);
    });
    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      console.log('[onboarding] avatar.speak_started');
      if (!disposed) setIsAvatarTalking(true);
    });
    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      console.log('[onboarding] avatar.speak_ended');
      if (!disposed) setIsAvatarTalking(false);
    });
    session.on(AgentEventsEnum.USER_SPEAK_STARTED, () => {
      console.log('[onboarding] user.speak_started');
    });
    session.on(AgentEventsEnum.USER_SPEAK_ENDED, () => {
      console.log('[onboarding] user.speak_ended');
    });
    session.on(AgentEventsEnum.USER_TRANSCRIPTION_CHUNK, (e) => {
      console.log('[onboarding] user.transcription.chunk', JSON.stringify(e?.text || ''));
    });
    session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION_CHUNK, (e) => {
      console.log('[onboarding] avatar.transcription.chunk len=%d', (e?.text || '').length);
    });
    // Final transcripts feed the onboarding bootstrap. Chunks (CHUNK events) are
    // streaming partial text — we only care about the finalized message so we
    // don't double-record.
    session.on(AgentEventsEnum.USER_TRANSCRIPTION, (e) => {
      console.log('[onboarding] user.transcription FINAL', JSON.stringify(e?.text || ''));
      ctxRef.current.recordOnboardingMessage?.('user', e?.text || '');
    });
    session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (e) => {
      console.log('[onboarding] avatar.transcription FINAL len=%d preview=%s', (e?.text || '').length, JSON.stringify((e?.text || '').slice(0, 80)));
      ctxRef.current.recordOnboardingMessage?.('ai', e?.text || '');
    });
    session.on(AgentEventsEnum.ELEVENLABS_AGENT_EVENT, (e) => {
      console.log('[onboarding] elevenlabs_agent_event', e?.elevenlabs_event_type, e?.data);
    });

    session.start().catch((e) => {
      console.error('LiveAvatarSession.start failed:', e);
      ctxRef.current.finalizeOnboardingTranscript?.();
    });

    return () => {
      disposed = true;
      try { session.removeAllListeners(); } catch {}
      try { session.voiceChat?.removeAllListeners?.(); } catch {}
      // .stop() returns a Promise — try/catch alone won't catch rejections.
      // We deliberately swallow them: a double-stop or already-disconnected
      // session legitimately races and can reject with "Session not found".
      try {
        const p = session.stop();
        if (p && typeof p.then === 'function') p.catch(() => {});
      } catch {}
      sessionRef.current = null;
    };
  }, [token]);

  // If the video element mounts after the stream is already ready (race), attach
  // when the ref becomes available.
  useEffect(() => {
    if (streamReady && videoRef.current && sessionRef.current) {
      try { sessionRef.current.attach(videoRef.current); } catch {}
    }
  }, [streamReady]);

  // Keep the transcript pinned to the latest message. Without this the user
  // has to scroll manually — new turns land below the visible viewport.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ctx.voiceTranscript.length]);

  // ── Chromakey: drop the avatar's green-screen background to transparent ──
  // Only runs for advisors flagged `chromakey: true`. Studio-rendered avatars
  // skip the WebGL pipeline and let the <video> show through directly.
  useEffect(() => {
    if (!useChromakey) return;
    if (!streamReady) return;
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
    });
    if (!gl) return;

    const VS = `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
      }
    `;
    // Chromakey via chrominance distance — robust to lighting variation on the
    // avatar's edges. threshold/smoothness tuned for HeyGen's pure-green key.
    // u_texScale/u_texOffset implement object-fit: cover by mapping the
    // canvas's [0,1]² coords into a centered, aspect-correct sub-rect of the
    // source video texture. Recomputed each frame so resizes / late-arriving
    // video dimensions are picked up automatically.
    // Two-stage keyer:
    //   1. Compute alpha from chrominance distance to the key color (smoothstep
    //      gives a feathered edge between threshold and threshold+smoothness).
    //   2. Spill suppression — pixels that survive the key but still have a
    //      residual green tint (green > max(red, blue)) get their green channel
    //      clamped to max(red, blue), neutralizing the halo without darkening
    //      the surviving luminance.
    const FS = `
      precision mediump float;
      uniform sampler2D u_image;
      uniform vec3 u_keyColor;
      uniform float u_threshold;
      uniform float u_smoothness;
      uniform vec2 u_texScale;
      uniform vec2 u_texOffset;
      varying vec2 v_texCoord;
      void main() {
        vec2 uv = v_texCoord * u_texScale + u_texOffset;
        vec4 c = texture2D(u_image, uv);

        float Yk = 0.299 * u_keyColor.r + 0.587 * u_keyColor.g + 0.114 * u_keyColor.b;
        float Crk = u_keyColor.r - Yk;
        float Cbk = u_keyColor.b - Yk;
        float Y  = 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
        float Cr = c.r - Y;
        float Cb = c.b - Y;
        float d  = sqrt((Cb - Cbk) * (Cb - Cbk) + (Cr - Crk) * (Cr - Crk));
        float a  = smoothstep(u_threshold, u_threshold + u_smoothness, d);

        // Spill suppression: clamp green to no more than the brighter of the
        // red/blue channels. Standard "Pacific" / luminance-preserving recipe.
        float maxRB = max(c.r, c.b);
        vec3 rgb = vec3(c.r, min(c.g, maxRB), c.b);

        gl_FragColor = vec4(rgb * a, a);
      }
    `;

    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error('chromakey shader compile error:', gl.getShaderInfoLog(sh));
        gl.deleteShader(sh);
        return null;
      }
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, VS);
    const fs = compile(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) return;

    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('chromakey program link error:', gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,   1, -1,  -1,  1,
      -1,  1,   1, -1,   1,  1,
    ]), gl.STATIC_DRAW);
    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
       0, 1,   1, 1,   0, 0,
       0, 0,   1, 1,   1, 0,
    ]), gl.STATIC_DRAW);

    const positionLoc = gl.getAttribLocation(program, 'a_position');
    const texCoordLoc = gl.getAttribLocation(program, 'a_texCoord');
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.enableVertexAttribArray(texCoordLoc);
    gl.vertexAttribPointer(texCoordLoc, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    gl.uniform1i(gl.getUniformLocation(program, 'u_image'),       0);
    gl.uniform3f(gl.getUniformLocation(program, 'u_keyColor'),    0.0, 1.0, 0.0); // pure green
    gl.uniform1f(gl.getUniformLocation(program, 'u_threshold'),   0.40);
    gl.uniform1f(gl.getUniformLocation(program, 'u_smoothness'),  0.18);
    const texScaleLoc  = gl.getUniformLocation(program, 'u_texScale');
    const texOffsetLoc = gl.getUniformLocation(program, 'u_texOffset');
    gl.uniform2f(texScaleLoc,  1, 1);
    gl.uniform2f(texOffsetLoc, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr  = window.devicePixelRatio || 1;
      const w    = Math.max(1, Math.floor(rect.width  * dpr));
      const h    = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width  = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };
    resize();
    window.addEventListener('resize', resize);

    let rafId = 0;
    let alive = true;
    const render = () => {
      if (!alive) return;
      if (video.readyState >= 2 && video.videoWidth > 0) {
        resize();
        // object-fit: cover — center-crop video into the canvas's aspect
        // ratio so the avatar isn't squeezed when the dims don't match.
        const canvasAR = canvas.width / canvas.height;
        const videoAR  = video.videoWidth / video.videoHeight;
        let sx = 1, sy = 1, ox = 0, oy = 0;
        if (videoAR > canvasAR) {
          sx = canvasAR / videoAR;
          ox = (1 - sx) / 2;
        } else if (videoAR < canvasAR) {
          sy = videoAR / canvasAR;
          oy = (1 - sy) / 2;
        }
        gl.uniform2f(texScaleLoc,  sx, sy);
        gl.uniform2f(texOffsetLoc, ox, oy);

        gl.bindTexture(gl.TEXTURE_2D, texture);
        // Our texCoord buffer is already pre-flipped on the V axis, so we
        // upload the source as-is (no FLIP_Y double-flip).
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        try {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
          gl.clear(gl.COLOR_BUFFER_BIT);
          gl.drawArrays(gl.TRIANGLES, 0, 6);
        } catch (e) {
          // Sporadic upload errors during stream renegotiation — skip frame.
        }
      }
      rafId = requestAnimationFrame(render);
    };
    render();

    return () => {
      alive = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      try { gl.deleteProgram(program); } catch {}
      try { gl.deleteShader(vs); } catch {}
      try { gl.deleteShader(fs); } catch {}
      try { gl.deleteBuffer(positionBuffer); } catch {}
      try { gl.deleteBuffer(texCoordBuffer); } catch {}
      try { gl.deleteTexture(texture); } catch {}
    };
  }, [streamReady, useChromakey]);

  const isConnecting =
    sessionState === SessionState.INACTIVE || sessionState === SessionState.CONNECTING;
  const isProcessing = ctx.isBootstrapping;
  const hasError     = Boolean(ctx.bootstrapError);

  // Only surface text for things the avatar's own visuals can't convey
  // (errors, the post-conversation Opus bootstrap). Connecting / Listening /
  // Speaking are handled by the frame overlay + glow + lip-sync.
  const statusText =
    isProcessing ? 'Building your strategy…'
    : hasError ? (ctx.bootstrapError || 'Something went wrong')
    : null;

  const handleEnd = () => {
    // Just clear the onboarding state — the cleanup effect (keyed on `token`)
    // is what actually stops the session. Calling session.stop() here too
    // would result in a double-stop, and the SDK rejects the second call
    // with "Session not found".
    ctx.finalizeOnboardingTranscript?.();
  };

  return (
    <>
      <div className={[
        'avatar-frame',
        useChromakey && 'avatar-frame--chromakey',
        isAvatarTalking && 'avatar-frame--speaking',
        isFullscreen && 'avatar-frame--fullscreen',
      ].filter(Boolean).join(' ')}>
        {/* The <video> always stays in the DOM (the SDK attaches the WebRTC
            stream to it). When chromakey is on, CSS hides it so the canvas
            shows; when chromakey is off, the video is the primary surface. */}
        <video
          ref={videoRef}
          className={`avatar-frame__video ${sessionState === SessionState.CONNECTED ? 'avatar-frame__video--live' : ''}`}
          autoPlay
          playsInline
          muted={false}
        />
        {useChromakey && (
          <canvas
            ref={canvasRef}
            className={`avatar-frame__canvas ${sessionState === SessionState.CONNECTED ? 'avatar-frame__canvas--live' : ''}`}
          />
        )}
        {sessionState !== SessionState.CONNECTED && (
          ctx.advisorPreviewUrl
            ? <img src={ctx.advisorPreviewUrl} alt="" className="avatar-frame__still" />
            : <div className="avatar-frame__placeholder" />
        )}
        {isConnecting && <div className="avatar-frame__overlay">Connecting…</div>}

        <button
          className={[
            'avatar-mic', 'avatar-mic--active',
            isAvatarTalking && 'avatar-mic--speaking',
            isConnecting && 'avatar-mic--processing',
          ].filter(Boolean).join(' ')}
          onClick={handleEnd}
          disabled={isConnecting}
          type="button"
          aria-label="End conversation"
        >
          <MdStop size={22} />
        </button>

        <button
          className="avatar-fullscreen-toggle"
          onClick={onToggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
          title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand to fullscreen'}
          type="button"
        >
          {isFullscreen ? <MdFullscreenExit size={22} /> : <MdFullscreen size={22} />}
        </button>
      </div>

      {statusText && <div className="voice-status">{statusText}</div>}

      {ctx.voiceTranscript.length > 0 && (
        <div className="voice-transcript" ref={transcriptRef}>
          {ctx.voiceTranscript.slice(-6).map((m, i) => (
            <div key={i} className={`voice-transcript__msg voice-transcript__msg--${m.role}`}>
              {m.text}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PreSessionView — shown before the user taps Start.
// ─────────────────────────────────────────────────────────────────────────────
function PreSessionView({ isFullscreen, onToggleFullscreen }) {
  const ctx = useAdvisor();
  const isProcessing = ctx.isBootstrapping;
  const hasError     = Boolean(ctx.bootstrapError);
  const liveAvatarLoading = ctx.liveAvatarLoading;
  const ctaDisabled = isProcessing || liveAvatarLoading;

  // The mic icon itself is the call to action — we only surface text when
  // there's something the user can't infer from the avatar visuals (errors,
  // post-conversation strategy build).
  const statusText =
    isProcessing ? 'Building your strategy…'
    : hasError ? (ctx.bootstrapError || 'Something went wrong')
    : ctx.liveAvatarError ? `⚠️ ${ctx.liveAvatarError}`
    : null;

  const showArrows = (ctx.advisors?.length || 0) > 1 && !liveAvatarLoading;

  return (
    <>
      <div className="advisor-carousel">
        <button
          className="advisor-arrow advisor-arrow--prev"
          onClick={() => ctx.prevAdvisor?.()}
          disabled={!showArrows}
          aria-label="Previous advisor"
          type="button"
        >
          <MdChevronLeft size={28} />
        </button>

        <div className={`avatar-frame ${isFullscreen ? 'avatar-frame--fullscreen' : ''}`}>
          {ctx.advisorPreviewUrl
            ? <img
                src={ctx.advisorPreviewUrl}
                alt={ctx.selectedAdvisor?.name || ''}
                className="avatar-frame__still"
                key={ctx.selectedAdvisor?.id}    /* re-mount on advisor change → image fades fresh */
              />
            : <div className="avatar-frame__placeholder" />}

          {ctx.selectedAdvisor?.name && (
            <div className="advisor-name-tag">{ctx.selectedAdvisor.name}</div>
          )}
          {ctx.advisors && ctx.advisors.length > 1 && (
            <div className="advisor-dots">
              {ctx.advisors.map((a, i) => (
                <span
                  key={a.id}
                  className={`advisor-dot ${i === ctx.selectedAdvisorIdx ? 'advisor-dot--active' : ''}`}
                />
              ))}
            </div>
          )}

          {liveAvatarLoading && <div className="avatar-frame__overlay">Connecting…</div>}

          <button
            className={[
              'avatar-mic',
              liveAvatarLoading && 'avatar-mic--processing',
            ].filter(Boolean).join(' ')}
            onClick={() => { if (!ctaDisabled) ctx.startOnboardingVoice(); }}
            disabled={ctaDisabled}
            type="button"
            aria-label="Start conversation"
          >
            <MdMic size={22} />
          </button>

          <button
            className="avatar-fullscreen-toggle"
            onClick={onToggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand to fullscreen'}
            type="button"
          >
            {isFullscreen ? <MdFullscreenExit size={22} /> : <MdFullscreen size={22} />}
          </button>
        </div>

        <button
          className="advisor-arrow advisor-arrow--next"
          onClick={() => ctx.nextAdvisor?.()}
          disabled={!showArrows}
          aria-label="Next advisor"
          type="button"
        >
          <MdChevronRight size={28} />
        </button>
      </div>

      {statusText && <div className="voice-status">{statusText}</div>}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WelcomeBackView — shown to returning users (memory present). Offers a fast
// path straight to Home that skips the cold-start voice onboarding.
// ─────────────────────────────────────────────────────────────────────────────
function WelcomeBackView({ onContinue }) {
  const ctx = useAdvisor();
  const memory = ctx.memory;
  const advisor = (ctx.advisors || []).find(a => a.id === memory?.selectedAdvisor) || ctx.advisors?.[0];

  const lastSession = memory?.lastSession?.slice(0, 10);
  const concernsText = memory?.concerns?.length
    ? `Last we spoke, you flagged ${memory.concerns.slice(0, 2).join(' and ')}.`
    : null;

  return (
    <>
      <div className="avatar-frame avatar-frame--welcome">
        {ctx.advisorPreviewUrl
          ? <img src={ctx.advisorPreviewUrl} alt={advisor?.name || ''} className="avatar-frame__still" />
          : <div className="avatar-frame__placeholder" />}
        {advisor?.name && (
          <div className="advisor-name-tag">{advisor.name}</div>
        )}
      </div>

      <div className="welcome-back">
        <h2 className="welcome-back__title">Welcome back.</h2>
        <p className="welcome-back__body">
          Picking up your conversation with <strong>{advisor?.name || 'your advisor'}</strong>
          {lastSession ? <> from <strong>{lastSession}</strong></> : null}.
          {concernsText && <><br />{concernsText}</>}
        </p>
        <button
          className="avatar-cta welcome-back__continue"
          onClick={onContinue}
          type="button"
        >
          Continue to Home →
        </button>
        <button
          className="welcome-back__forget"
          onClick={() => {
            if (window.confirm('Forget this account and start onboarding from scratch? Your stored memory will be cleared.')) {
              ctx.resetMemory();
              window.location.reload();
            }
          }}
          type="button"
        >
          Forget me and start over
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level page.
// ─────────────────────────────────────────────────────────────────────────────
export default function Advisor() {
  const ctx = useAdvisor();
  const navigate = useNavigate();
  const isActive = ctx.voiceActivePage === 'onboarding' && Boolean(ctx.liveAvatarToken);
  const finalizeRef = useRef(ctx.finalizeOnboardingTranscript);
  finalizeRef.current = ctx.finalizeOnboardingTranscript;
  const activePageRef = useRef(ctx.voiceActivePage);
  activePageRef.current = ctx.voiceActivePage;

  const isReturning = ctx.isReturningUser && !isActive && !ctx.isBootstrapping;

  // Fullscreen avatar mode — collapses chrome, blows the avatar up to fill
  // the viewport. Esc exits.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => setIsFullscreen(v => !v);
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  // If the user navigates away mid-conversation, end onboarding so the
  // bootstrap fires (or is silently skipped if no transcript yet).
  useEffect(() => {
    return () => {
      if (activePageRef.current === 'onboarding') {
        finalizeRef.current?.();
      }
    };
  }, []);

  const handleContinue = async () => {
    // Returning users skip onboarding and the cold-start strategy build.
    // resumeFromMemory rebuilds the deployed-strategy state directly from
    // persisted profile/tilts/allocation — no Opus call, no Strategy-page
    // detour — so Home populates immediately. (Falls back internally to
    // bypassOnboarding if memory is missing the strategy fields.)
    if (!ctx.deployed) await ctx.resumeFromMemory();
    setTimeout(() => navigate('/home'), 50);
  };

  return (
    <div className={`advisor advisor--solo advisor--voice ${isFullscreen ? 'advisor--fullscreen' : ''}`}>
      <div className="voice-surface">
        <div className="voice-brand">Puro Wealth · AI Advisor</div>
        {isActive ? (
          <ActiveAvatarSession isFullscreen={isFullscreen} onToggleFullscreen={toggleFullscreen} />
        ) : isReturning ? (
          <WelcomeBackView onContinue={handleContinue} />
        ) : (
          <PreSessionView isFullscreen={isFullscreen} onToggleFullscreen={toggleFullscreen} />
        )}
      </div>

      {!isActive && !ctx.isBootstrapping && !isReturning && !isFullscreen && (
        <button className="bypass-link" onClick={() => ctx.bypassOnboarding()}>
          Bypass onboarding
        </button>
      )}
    </div>
  );
}
